<script setup lang="ts">
interface Props {
  dynamicId?: string;
  value?: { name: string; value: string | number; [key: string]: unknown };
  width?: string;
  index?: number;
  title?: string;
  options: { name: string; value: string | number; [key: string]: unknown }[];
  errorMessage?: string;
  isRequired?: boolean;
  placeholder?: string;
  editable?: boolean;
  clearable?: boolean;
  fixed?: boolean;
  disabled?: boolean;
  searchable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  dynamicId: "",
  value: () => ({ name: "", value: "" }),
  width: "100%",
  index: 0,
  title: "",
  errorMessage: "",
  isRequired: false,
  placeholder: "Please select an option",
  editable: false,
  clearable: false,
  fixed: false,
  disabled: false,
  searchable: false,
});

const emit = defineEmits<{
  returnValue: [option: { name: string; value: string | number; [key: string]: unknown } | string];
  addNew: [value: boolean];
  returnSearch: [term: string];
}>();

const showOptions = ref(false);
const localValue = ref(props.value);
const optionsToShow = ref([...props.options]);
const searchTerm = ref("");

const showError = computed(() => props.errorMessage.length > 0);

watch(
  () => props.value,
  (to) => {
    localValue.value = to;
    emit("returnValue", to);
  },
);

watch(
  () => props.options,
  (to) => {
    optionsToShow.value = [...to];
  },
);

onMounted(() => {
  optionsToShow.value = [...props.options];
});

function updateOption(option: { name: string; value: string | number; [key: string]: unknown }) {
  showOptions.value = false;
  localValue.value = option;
  resetSearch();
  emit("returnValue", option);
}

function resetSearch() {
  searchTerm.value = "";
  optionsToShow.value = [...props.options];
}

function addNew() {
  emit("addNew", true);
}

function toggleOptions() {
  showOptions.value = !showOptions.value;
  if (!showOptions.value) resetSearch();
}

function clearValue() {
  emit("returnValue", "");
}

function closeDropdown() {
  showOptions.value = false;
  resetSearch();
}

function updateSearchTerm(searchTerm: string) {
  if (searchTerm) {
    optionsToShow.value = props.options.filter((option) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    emit("returnSearch", searchTerm);
  } else {
    optionsToShow.value = [...props.options];
  }
}
</script>

<template>
  <div
    class="dropdown"
    :style="`width: ${width}`"
  >
    <span
      v-if="title"
      class="dropdown__title"
    >
      {{ title }}
      <i v-if="isRequired">*</i>
    </span>

    <div
      class="dropdown__wrapper"
      :style="showOptions && 'border-bottom: 2px solid white; border-radius: 0.4rem 0.4rem 0 0'"
      :class="[showError && 'input-error', disabled && 'disabled']"
    >
      <div
        v-show="localValue && clearable"
        class="dropdown__wrapper--icon"
        :style="showOptions && 'border-bottom: none; border-radius: 0.4rem 0.4rem 0 0.4rem'"
      >
        <fa
          :icon="['fal', 'xmark']"
          @click="clearValue"
        />
      </div>

      <div
        :id="dynamicId"
        class="dropdown__wrapper--select"
        @click="!disabled && toggleOptions()"
      >
        <span
          class="chosen small-text"
          :class="localValue?.name ? 'grey-text' : 'light-grey-text'"
          disabled
          selected
        >
          {{ localValue?.name || placeholder }}
        </span>
      </div>
      <div
        class="dropdown__wrapper--icon"
        :style="showOptions && 'border-bottom: none; border-radius: 0.4rem 0.4rem 0 0.4rem'"
        @click="!disabled && toggleOptions()"
      >
        <fa
          v-if="!disabled"
          :icon="['fal', `chevron-${showOptions ? 'up' : 'down'}`]"
        />
      </div>
    </div>
    <div
      v-show="showOptions"
      class="dropdown__options"
      :style="`position: ${fixed ? 'fixed' : 'absolute'}`"
    >
      <input
        v-if="searchable"
        v-model="searchTerm"
        class="dropdown__options--search small-text"
        type="text"
        autocomplete="off"
        placeholder="Search..."
        @click.stop
        @input="updateSearchTerm(searchTerm)"
      />
      <p
        v-for="(option, i) in optionsToShow"
        :key="`option_${i}`"
        class="link small-text"
        @click="updateOption(option)"
      >
        {{ option.name }}
      </p>
      <div
        v-if="editable"
        class="dropdown__options--add"
      >
        <p
          class="link small-text"
          @click="addNew"
        >
          Add New +
        </p>
      </div>
    </div>
    <BannerError :message="showError ? errorMessage : ''" />
  </div>
</template>

<style lang="scss">
.dropdown {
  display: block;
  position: relative;
  &__title {
    display: flex;
    align-items: center;
    color: var(--color-grey-1);
    i {
      color: var(--color-pink-1);
    }
  }
  &__wrapper {
    display: flex;
    border: 2px solid var(--color-grey-3);
    border-radius: 0.4rem;
    width: 100%;
    height: 2.8rem;
    box-sizing: border-box;
    &--select {
      cursor: pointer;
      position: relative;
      display: flex;
      align-items: center;
      flex: 1;
      border-right: none !important;
      appearance: none;
      padding: 0 0.5rem 0 0.65rem;
      color: var(--color-grey-2);
      font-family: "GalanoGrotesqueMedium", sans-serif;
      border-radius: 0.4rem;
      font-size: 0.8rem;
      span {
        line-height: 0.8rem !important;
      }
      &:focus {
        outline: none;
      }
      .chosen {
        margin: 0;
      }
    }
    &--icon {
      border-radius: 0.4rem;
      color: var(--color-grey-2) !important;
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 0 0.7rem;
      svg {
        color: var(--color-grey-2);
        margin-left: 10px;
      }
      @media (max-width: $tablet) {
        margin: 0 -2px 0 0;
        width: 30px;
      }
    }
  }
  &__options {
    background-color: var(--color-white-1);
    appearance: none;
    padding: 0.5rem;
    color: var(--color-grey-2);
    border: 2px solid var(--color-grey-3);
    border-top: none;
    margin-bottom: 1rem;
    font-family: "GalanoGrotesqueMedium", sans-serif;
    font-size: 0.7rem;
    border-radius: 0 0 0.4rem 0.4rem;
    width: 100%;
    box-sizing: border-box;
    z-index: 5555;
    // Cap the list height and scroll internally — otherwise a long set of
    // options (e.g. cost centres on a large org) extends past the viewport
    // and covers the Next/Submit button with no way to scroll past it. The
    // 15rem ceiling matches the address-results dropdown in LocationBox.
    max-height: 15rem;
    overflow-y: auto;
    &--search {
      width: 100%;
      box-sizing: border-box;
      margin-bottom: 0.5rem;
      padding: 0.5rem 0.65rem;
      border: 2px solid var(--color-grey-3);
      border-radius: 0.4rem;
      color: var(--color-grey-2);
      font-family: "GalanoGrotesqueMedium", sans-serif;
      &:focus {
        outline: none;
        border-color: var(--color-pink-1);
      }
    }
  }
  &__new {
    width: 50%;
    &--text {
      margin: 0.5rem 0;
    }
  }
}
</style>
